import React, { useContext, useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import AppContext from '../../../Context/AppContext';
import requestAxios from '../../../util/requestAxios';
import { loadElearningModules } from '../elearningCache';
import {
    PROGRAM_CONFIG,
    PROGRAM_IDS,
} from './programConfig';
import ProgramHub from './ProgramHub';
import ModulePlayer from './ModulePlayer';
import './ELearningWorkspace.css';

/**
 * In-panel e-learning workspace for Dashboard V2.
 * Replaces metrics / tracker / goals / strip while open.
 */
const ELearningWorkspace = ({
    programId = PROGRAM_IDS.BREAKTHROUGH,
    initialModuleId = null,
    initialCourse = null,
    onBack,
    onProgressRefresh,
}) => {
    const history = useHistory();
    const { checkUserSession } = useContext(AppContext);
    const session = checkUserSession() || {};
    const { token, userId } = session;
    const config = PROGRAM_CONFIG[programId] || PROGRAM_CONFIG[PROGRAM_IDS.BREAKTHROUGH];

    const [view, setView] = useState('hub'); // hub | player
    const [modules, setModules] = useState([]);
    const [breakthroughModules, setBreakthroughModules] = useState([]);
    const [courses, setCourses] = useState([]);
    const [selectedCourse, setSelectedCourse] = useState(null);
    const [hubLoading, setHubLoading] = useState(true);
    const [hubError, setHubError] = useState('');

    const [activeModule, setActiveModule] = useState(null);
    const [detail, setDetail] = useState(null);
    const [pendingQuestions, setPendingQuestions] = useState(null);
    const [playerLoading, setPlayerLoading] = useState(false);
    const [playerError, setPlayerError] = useState('');
    const [busy, setBusy] = useState(false);
    const didAutoOpen = useRef(false);
    const didLoadCourse = useRef(false);

    const userStepId = parseInt(
        session.userStepId || localStorage.getItem('userStepId') || '0',
        10
    );
    const isActiveCoach = localStorage.getItem('isActiveCoach') === 'true';
    const userTypeId = session.userTypeId;
    const needsSteps =
        programId === PROGRAM_IDS.BREAKTHROUGH &&
        userStepId < 4 &&
        userTypeId !== 4 &&
        !isActiveCoach;

    const loadHub = useCallback(async () => {
        if (!token || !userId) return;
        setHubLoading(true);
        setHubError('');

        try {
            if (config.isAdditional) {
                const { error, data } = await requestAxios({
                    url: config.listCourses,
                    token,
                });
                if (error) {
                    setHubError('Could not load programs.');
                    setCourses([]);
                } else {
                    setCourses(Array.isArray(data) ? data : []);
                }
                setModules([]);
            } else {
                const { error, data } = await loadElearningModules(
                    userId,
                    token,
                    { force: true }
                );
                if (error || !data) {
                    setHubError('Could not load modules.');
                    setModules([]);
                    setBreakthroughModules([]);
                } else {
                    const list = Array.isArray(data[config.modulesKey])
                        ? data[config.modulesKey]
                        : [];
                    setModules(list);
                    if (config.breakthroughKey) {
                        setBreakthroughModules(
                            Array.isArray(data[config.breakthroughKey])
                                ? data[config.breakthroughKey]
                                : []
                        );
                    }
                }
            }
        } finally {
            setHubLoading(false);
        }
    }, [token, userId, config]);

    const loadCourseModules = useCallback(
        async (course) => {
            if (!token || !course?.id) return;
            setHubLoading(true);
            setHubError('');
            setSelectedCourse(course);
            const { error, data } = await requestAxios({
                url: config.listModules(course.id),
                token,
            });
            if (error || !data) {
                setModules([]);
                setHubError('Could not load course modules.');
            } else {
                const list = Array.isArray(data.modules) ? data.modules : [];
                setModules(list);
            }
            setHubLoading(false);
        },
        [token, config]
    );

    useEffect(() => {
        setView('hub');
        setActiveModule(null);
        setDetail(null);
        setSelectedCourse(null);
        setPendingQuestions(null);
        didAutoOpen.current = false;
        didLoadCourse.current = false;
        loadHub();
    }, [programId, loadHub]);

    useEffect(() => {
        if (
            !config.isAdditional ||
            !initialCourse ||
            hubLoading ||
            didLoadCourse.current
        ) {
            return;
        }
        didLoadCourse.current = true;
        loadCourseModules(initialCourse);
    }, [config.isAdditional, initialCourse, hubLoading, loadCourseModules]);

    const openModule = async (module) => {
        setActiveModule(module);
        setView('player');
        setPlayerLoading(true);
        setPlayerError('');
        setPendingQuestions(null);
        setDetail(null);

        if (config.isAdditional) {
            localStorage.setItem('isAditionalCourse', '1');
        } else {
            localStorage.removeItem('isAditionalCourse');
        }
        localStorage.removeItem('isCertificationModule');
        localStorage.setItem('ModuleId', String(module.id));

        const { error, data } = await requestAxios({
            url: config.detailModule(module.id),
            token,
        });
        setPlayerLoading(false);
        if (error || !data) {
            setPlayerError('Could not load this module.');
            return;
        }
        setDetail(data);
        if (Array.isArray(data.questions) && data.questions.length > 0) {
            setPendingQuestions(data.questions);
        }
    };

    useEffect(() => {
        if (
            !initialModuleId ||
            hubLoading ||
            didAutoOpen.current ||
            !modules.length
        ) {
            return;
        }
        const mod = modules.find(
            (m) =>
                m.id === initialModuleId ||
                String(m.id) === String(initialModuleId)
        );
        if (mod && mod.isUnlocked !== false) {
            didAutoOpen.current = true;
            openModule(mod);
        }
        // openModule is stable enough for one-shot auto-open
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialModuleId, hubLoading, modules]);

    const saveCustomize = async (assesmentDetails) => {
        if (!config.saveCustomize) {
            setPendingQuestions(null);
            return;
        }
        setBusy(true);
        const { error } = await requestAxios({
            url: config.saveCustomize,
            method: 'post',
            token,
            data: {
                ModuleId: activeModule.id,
                AssesmentDetails: assesmentDetails,
            },
        });
        setBusy(false);
        if (error) {
            setPlayerError('Could not save answers. Try again.');
            return;
        }
        setPendingQuestions(null);
        setPlayerLoading(true);
        const res = await requestAxios({
            url: config.detailModule(activeModule.id),
            token,
        });
        setPlayerLoading(false);
        if (!res.error && res.data) {
            setDetail(res.data);
            if (Array.isArray(res.data.questions) && res.data.questions.length) {
                setPendingQuestions(res.data.questions);
            }
        }
    };

    const completeModule = async () => {
        if (!activeModule || busy) return;
        setBusy(true);
        const { error } = await requestAxios({
            url: config.completeModule,
            method: 'put',
            token,
            data: { Id: activeModule.id, userId },
        });
        setBusy(false);
        if (error) {
            setPlayerError('Could not complete module. Try again.');
            return;
        }
        if (initialModuleId && typeof onBack === 'function') {
            onBack();
            if (typeof onProgressRefresh === 'function') {
                onProgressRefresh();
            }
            return;
        }
        setView('hub');
        setActiveModule(null);
        setDetail(null);
        if (config.isAdditional && selectedCourse) {
            await loadCourseModules(selectedCourse);
        } else {
            await loadHub();
        }
        if (typeof onProgressRefresh === 'function') {
            onProgressRefresh();
        }
    };

    const backFromPlayer = () => {
        // Opened directly from My E-Learning hub → return there
        if (initialModuleId) {
            if (typeof onBack === 'function') onBack();
            return;
        }
        setView('hub');
        setActiveModule(null);
        setDetail(null);
        setPendingQuestions(null);
        setPlayerError('');
    };

    const activeModuleIndex = activeModule
        ? modules.findIndex((m) => m.id === activeModule.id)
        : -1;
    const activeModuleNumber =
        activeModuleIndex >= 0 ? activeModuleIndex + 1 : 1;

    return (
        <section
            className={`dv2-el dv2-glass dv2-el--strong${
                view === 'player' ? ' dv2-el--player' : ''
            }`}
        >
            {view !== 'player' ? (
                <div className="dv2-el__top">
                    <button
                        type="button"
                        className="dv2-el__back"
                        onClick={onBack}
                    >
                        ← Back to E-Learning
                    </button>
                    <div className="dv2-el__top-titles">
                        <p className="dv2-el__program-label">PROGRAM</p>
                        <h2 className="dv2-section-title">{config.title}</h2>
                    </div>
                </div>
            ) : null}

            {needsSteps ? (
                <div className="dv2-el__empty">
                    <h3>{config.title}</h3>
                    <p>{config.stepsGateCopy}</p>
                    <button
                        type="button"
                        className="dv2-el__cta"
                        onClick={() => history.push(config.stepsPath)}
                    >
                        Go to Design My Life Blueprint
                    </button>
                </div>
            ) : view === 'player' ? (
                <ModulePlayer
                    moduleMeta={activeModule}
                    detail={detail}
                    loading={playerLoading}
                    errorMsg={playerError}
                    busy={busy}
                    programTitle={config.title}
                    moduleNumber={activeModuleNumber}
                    modulesTotal={modules.length || 1}
                    onBack={backFromPlayer}
                    onBackToDashboard={onBack}
                    onComplete={completeModule}
                    questions={pendingQuestions}
                    onSaveCustomize={saveCustomize}
                />
            ) : (
                <ProgramHub
                    config={config}
                    modules={modules}
                    breakthroughModules={breakthroughModules}
                    courses={courses}
                    selectedCourse={selectedCourse}
                    loading={hubLoading}
                    errorMsg={hubError}
                    onOpenModule={openModule}
                    onSelectCourse={loadCourseModules}
                    onBackToCourses={() => {
                        setSelectedCourse(null);
                        setModules([]);
                        loadHub();
                    }}
                />
            )}
        </section>
    );
};

export default ELearningWorkspace;
